import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { formatDate } from '@/utils/date';

import { NoteItemForm } from './note-item-form';
import { useNoteItemActions } from './use-note-item-actions';

import type { NoteItem } from './assistant-notes.types';

// The marker the agent reads carries the month of the same instant
const lastEvidenceAt = (item: NoteItem): string =>
  item.confirmed_at !== null &&
  Date.parse(item.confirmed_at) > Date.parse(item.changed_at)
    ? item.confirmed_at
    : item.changed_at;

type NotesItemRowProps = {
  /** The note as the server has it, or, while its form is open, as it was when the form opened */
  item: NoteItem;
  editing: boolean;
  onStartEditing: () => void;
  onStopEditing: () => void;
};

/** One note: its text, its date, its source chat, and the edit and delete actions */
export const NotesItemRow = ({
  item,
  editing,
  onStartEditing,
  onStopEditing,
}: NotesItemRowProps) => {
  const actions = useNoteItemActions(item, onStopEditing);

  if (editing) {
    return (
      <div role="listitem">
        <NoteItemForm
          label="Note text"
          initialText={item.text}
          submitLabel="Save"
          isPending={actions.isSaving}
          onSubmit={actions.save}
          onCancel={actions.cancelEditing}
        />
      </div>
    );
  }
  return (
    <XStack role="listitem" className="w-full items-start gap-3">
      <YStack className="min-w-0 flex-1 gap-1">
        <p className="whitespace-pre-wrap break-words text-sm text-foreground">
          {item.text}
        </p>
        <XStack className="items-center gap-3 text-xs text-foreground-muted">
          <span>{formatDate(lastEvidenceAt(item), 'MMM d, yyyy')}</span>
          {item.source_chat_id && (
            <Button
              variant="link"
              size="xs"
              textSize="xs"
              className="h-auto p-0"
              isLoading={actions.isOpeningChat}
              onClick={actions.openSourceChat}
            >
              Open chat
            </Button>
          )}
        </XStack>
      </YStack>
      <XStack className="shrink-0 items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          iconOnly
          aria-label="Edit note"
          title="Edit"
          onClick={onStartEditing}
        >
          <Icons.pencil />
        </Button>
        <Button
          variant="destructive-ghost"
          size="icon"
          iconOnly
          aria-label="Delete note"
          title="Delete"
          isLoading={actions.isDeleting}
          onClick={() => void actions.remove()}
        >
          <Icons.trash2 />
        </Button>
      </XStack>
    </XStack>
  );
};
